import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import { Box, Grid2 as Grid, IconButton, MenuItem, Paper, Select, Stack, Typography, useMediaQuery } from "@mui/material";
import moment from "moment";
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StatCard from "../../components/card/StatCard";
import { ComingSoonOverlay, ComingSoonText } from "../../components/navigation/LinkRoutes";
import DataTable, { IColumn, TData } from "../../components/table/data/DataTable";
import usePeriodicColumns from "../../components/table/data/DataTableColumns";
import { default as IBuilding, default as IBuilidng } from "../../models/building";
import { EPeriodicStatus } from "../../models/periodic/periodic";
import auth from "../../services/auth/AuthService";
import periodidService from "../../services/periodic/PeriodicService";
import DeepSet from "../../utils/deepSet";
import PeriodicStateChart from "./chart/periodic/periodicStateChart";
import QuantityPeriodic from "./chart/periodic/quantityPeriodic";

interface PeriodicDisplayDate {
    status: EPeriodicStatus;
    scheduleDate: Date;
}

interface PeriodicDisplay {
    id: string;
    room: string;
    building: IBuilidng;
    floor: string;
    profile: string;
    taskId: number;
    taskName: string;
    cleaningDate: PeriodicDisplayDate;
}

interface Comment {
    text: string;
    author: string;
    date: Date;
}

interface CardWasteProps {
    title: string;
    value: number;
    icon: ReactNode;
    totalValue?: number;
    valueColor?: string;
}

interface PeriodicCountType {
    todoCount: number;
    acceptedCount: number;
    toverifyCount: number;
    overdueCount: number;
    refusedCount: number;
    totalCount: number;
}

interface ProfileStatusCounts {
    [profile: string]: {
        category: string;
        refusedCount: number;
        todoCount: number;
        overdueCount: number;
        toverifyCount: number;
    };
}

interface PeriodicCategoryData {
    category: string;
    refusedCount: number;
    todoCount: number;
    overdueCount: number;
    toverifyCount: number;
}

const PeriodicCard = ({ title, value, totalValue, icon, valueColor }: CardWasteProps) => {
    return (
        <Paper style={{ width: "100%", height: "100%" }}>
            <Box padding={1} paddingLeft={2}>
                {icon}
                <Stack sx={{ marginTop: 1 }} direction="row" alignItems="center" gap={1}>
                    <Typography>{title}</Typography>
                </Stack>
                <Typography sx={{ marginTop: 1, fontSize: '24px', color: valueColor, fontWeight: 'bold' }}>
                    {totalValue ? `${value}/${totalValue}` : value}
                </Typography>
            </Box>
        </Paper>
    );
};

interface ToolbarProps {
    onFilterClick: () => void;
}

const Toolbar = ({ onFilterClick }: ToolbarProps) => {
    return (
        <Box
            display="flex"
            justifyContent="space-between"
            component={Paper}
            sx={{ width: "100%", padding: 1, marginBottom: 1, paddingX: 2 }}
        >
            <IconButton onClick={onFilterClick}>
                <FilterAltIcon></FilterAltIcon>
            </IconButton>
        </Box>
    );
};

const comments: Comment[] = [
    {
        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui labore odio nostrum veniam excepturi maiores, error numquam! Unde enim iste eligendi quas facilis quidem consequatur ad aperiam voluptatem, ex officia!",
        author: "Nadia Fontaine",
        date: new Date("2024-03-24T15:02"),
    },
    {
        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui labore odio nostrum veniam excepturi maiores, error numquam! Unde enim iste eligendi quas facilis quidem consequatur ad aperiam voluptatem, ex officia!",
        author: "Mélanie Marier",
        date: new Date("2024-03-27T08:47"),
    },
    {
        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui labore odio nostrum veniam excepturi maiores, error numquam! Unde enim iste eligendi quas facilis quidem consequatur ad aperiam voluptatem, ex officia!",
        author: "Karl Bédard",
        date: new Date("2024-03-30T10:30"),
    },
    {
        text: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Qui labore odio nostrum veniam excepturi maiores, error numquam! Unde enim iste eligendi quas facilis quidem consequatur ad aperiam voluptatem, ex officia!",
        author: "Mélanie Marier",
        date: new Date("2024-04-02T14:55"),
    },
];

const PeriodicDashboardPage = () => {
    const isPC = useMediaQuery("(min-height:800px) and (min-width:1400px)");
    const smValue = isPC ? 6 : 12;
    const [data, setData] = useState<PeriodicDisplay[]>([]);
    const [periodicCategoryData, setPeriodicCategoryData] = useState<PeriodicCategoryData[]>([]);
    const [isFilterDisable, setIsFilterDisable] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [buildings, setBuildings] = useState<IBuilidng[]>([]);
    const [selectedBuildingsIds, setSelectedBuildingsIds] = useState<number[]>([]);
    const columns = usePeriodicColumns({
        includeBuilding: true,
        includeRoom: true,
        includeFloor: true,
        includeProfile: true,
        includeTaskName: true,
    });
    const { t } = useTranslation(["periodical,common"]);
    const [periodicCount, setPeriodicCount] = useState<PeriodicCountType>({
        todoCount: 0,
        acceptedCount: 0,
        toverifyCount: 0,
        overdueCount: 0,
        refusedCount: 0,
        totalCount: 0,
    })

    useEffect(() => {
        periodidService
            .GetPeriodics(auth.user?.companyId || 0)
            .then((periodics) => {
                if (!periodics) return;

                // Collect unique buildings
                const uniqueBuildings = new DeepSet<IBuilding>();
                periodics.forEach((periodic) => {
                    uniqueBuildings.add(periodic.building);
                });

                // Filter data by selected building IDs
                const filtredData = selectedBuildingsIds.length === 0
                    ? periodics
                    : periodics.filter((o) =>
                        selectedBuildingsIds.includes(o.building.id)
                    );

                // Transform periodics data to display format
                const data: PeriodicDisplay[] = filtredData.map((periodic) => {
                    // Map a single schedule to cleaningDate (not an array)
                    const cleaningDate: PeriodicDisplayDate = {
                        scheduleDate: periodic.schedule.scheduleDate,
                        status: periodic.schedule.status,
                    };

                    return {
                        id: `${periodic.id}`,
                        building: periodic.building,
                        floor: periodic.floor.name,
                        room: periodic.local.name,
                        cleaningDate,  // Now it's a single cleaningDate instead of an array
                        profile: periodic.profile.name,
                        taskId: periodic.task.id,
                        taskName: periodic.task.name,
                    };
                });

                // Set unique buildings and sort the data by room name
                setBuildings(uniqueBuildings.values());
                setData(data.sort((a, b) => (a.room > b.room ? 1 : -1)));
            });
    }, [selectedBuildingsIds]);


    useEffect(() => {
        const dateCounts = new Map<string, number>();
        const statusActions = new Map<EPeriodicStatus, number>([
            [EPeriodicStatus.Todo, 0],
            [EPeriodicStatus.Accepted, 0],
            [EPeriodicStatus.ToVerify, 0],
            [EPeriodicStatus.Refused, 0],
            [EPeriodicStatus.Overdue, 0],
        ]);
        const profileStatusCounts: ProfileStatusCounts = {};

        const filtredData = selectedBuildingsIds.length === 0 ? data :
            data.filter(o => selectedBuildingsIds.includes(o.building.id));

        filtredData.forEach((periodicDisplay) => {
            // Access the single cleaningDate object directly instead of iterating over an array
            const cleaningDate = periodicDisplay.cleaningDate;

            const profile = periodicDisplay.profile;

            // Initialize profile counts if not already done
            if (!profileStatusCounts[profile]) {
                profileStatusCounts[profile] = {
                    category: profile,
                    refusedCount: 0,
                    todoCount: 0,
                    overdueCount: 0,
                    toverifyCount: 0
                };
            }

            let status = cleaningDate.status;
            const dateString = moment.utc(cleaningDate.scheduleDate).format('YYYY-MM-DD');

            // Count statuses
            if (status === EPeriodicStatus.Overdue) {
                profileStatusCounts[profile].overdueCount += 1;
            }

            if (status === EPeriodicStatus.Todo) {
                profileStatusCounts[profile].todoCount += 1;
            } else if (status === EPeriodicStatus.Refused) {
                profileStatusCounts[profile].refusedCount += 1;
            } else if (status === EPeriodicStatus.ToVerify) {
                profileStatusCounts[profile].toverifyCount += 1;
            }

            // Count dates
            const dateCount = dateCounts.get(dateString) || 0;
            dateCounts.set(dateString, dateCount + 1);

            // Count status actions
            const currentCount = statusActions.get(status) || 0;
            statusActions.set(status, currentCount + 1);
        });

        // Convert profile status counts to array
        const periodicCategoryDataArray = Object.values(profileStatusCounts);
        setPeriodicCategoryData(periodicCategoryDataArray);

        // Total count of all periods
        const totalCount = Array.from(dateCounts.values()).reduce((acc, count) => acc + count, 0);

        setPeriodicCount({
            todoCount: statusActions.get(EPeriodicStatus.Todo) || 0,
            acceptedCount: statusActions.get(EPeriodicStatus.Accepted) || 0,
            toverifyCount: statusActions.get(EPeriodicStatus.ToVerify) || 0,
            refusedCount: statusActions.get(EPeriodicStatus.Refused) || 0,
            overdueCount: statusActions.get(EPeriodicStatus.Overdue) || 0,
            totalCount,
        });
        setIsLoading(false);
    }, [data, selectedBuildingsIds]);

    interface CustomTextProps {
        text: string;
        isBold?: boolean;
    }

    const CustomText = ({ text, isBold }: CustomTextProps) => {
        const textStyle: CSSProperties = {
            marginLeft: '1.5rem',
            fontWeight: isBold ? 'bold' : 'normal',
        };

        return (
            <Typography variant="body2" style={textStyle}>
                {text}
            </Typography>
        );
    }

    return (
        <Box sx={{
            overflow: isPC ? "hidden" : "auto",
            maxHeight: "88vh",
            '::-webkit-scrollbar': { width: 0 }
        }}>
            <Grid container spacing={3}>

                <Grid size={{ sm: 12, xs: 12 }}>
                    <Paper style={{ width: "100%", height: "100%" }}>
                        <Box display="flex" alignItems="center" padding={1} paddingLeft={2}>
                            <Typography sx={{ marginRight: 1 }}>{t("common:header.buildings")} </Typography>
                            <Select
                                multiple
                                value={selectedBuildingsIds}
                                onChange={({ target: { value } }) => {
                                    setSelectedBuildingsIds(typeof value === 'string' ? value.split(',').map((v) => Number(v)) : value);
                                }}
                                sx={{ width: "100%", height: "2em" }}
                                MenuProps={{
                                    style: { overflowY: "visible" },
                                }}
                            >
                                {buildings.map((building, index) => (
                                    <MenuItem key={index} value={building.id}>
                                        {building.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Paper>
                </Grid>


                {/* Left side */}
                <Grid size={{ sm: 3, xs: 12 }}>
                    <PeriodicCard
                        title={t('periodical:info.Accepted')}
                        value={periodicCount.acceptedCount}
                        totalValue={periodicCount.totalCount}
                        icon={<CalendarMonthIcon />}
                        valueColor="#94ca58"
                    />
                </Grid>
                <Grid size={{ sm: 3, xs: 12 }}>
                    <PeriodicCard
                        title={t('periodical:info.ToVerify')}
                        value={periodicCount.toverifyCount}
                        icon={<RuleFolderIcon />}
                        valueColor="#65a6fa"
                    />
                </Grid>
                <Grid size={{ sm: 3, xs: 12 }}>
                    <PeriodicCard
                        title={t('periodical:info.Overdue')}
                        value={periodicCount.overdueCount}
                        icon={<AssignmentLateIcon />}
                        valueColor="orange"
                    />
                </Grid>
                <Grid size={{ sm: 3, xs: 12 }}>
                    <PeriodicCard
                        title={t('periodical:info.Refused')}
                        value={periodicCount.refusedCount}
                        icon={<CancelIcon />}
                        valueColor="red"
                    />
                </Grid>
                <Grid size={{ sm: smValue, xs: 12 }} alignItems="flex-end">
                    <Toolbar
                        onFilterClick={() => setIsFilterDisable((old) => !old)}
                    />
                    <DataTable
                        disableFiltrer={isFilterDisable}
                        dense
                        rows={data}
                        isLoading={isLoading}
                        columns={columns as IColumn<TData<PeriodicDisplay>>[]}
                        style={{ overflow: "auto", height: isPC ? "54.8vh" : "auto" }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: smValue }} container direction="column">
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <StatCard title={t('periodical:title.pie')}>
                                <PeriodicStateChart
                                    data={{
                                        validatePercentage: periodicCount.acceptedCount,
                                        plannedPercentage: periodicCount.todoCount,
                                        verifiedPercentage: periodicCount.toverifyCount,
                                        latePercentage: periodicCount.overdueCount,
                                        refusedPercentage: periodicCount.refusedCount,
                                    }} />
                            </StatCard>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <StatCard title={t('periodical:title.bar')}>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    height="100%"
                                    marginTop={3}
                                >
                                    <QuantityPeriodic categoryDataArray={periodicCategoryData} />
                                </Box>
                            </StatCard>
                        </Grid>
                        <Grid>
                            <Paper sx={{ overflow: "hidden", height: isPC ? "31.7vh" : "auto", position: 'relative' }}>
                                <ComingSoonOverlay sx={{ overflow: "hidden" }}>
                                    <ComingSoonText variant="h5">{t("periodical:info.comingSoon")}</ComingSoonText>
                                </ComingSoonOverlay>
                                <Box padding={1} >
                                    <Typography variant="subtitle2">
                                        {t('periodical:info.Remarks')}
                                    </Typography>
                                    <br></br>
                                    {comments.map((commentaire, index) => (
                                        <Grid key={index}>
                                            <CustomText text={commentaire.text} isBold={false} />
                                            <CustomText text={`${commentaire.author} - ${moment(commentaire.date).format('LLL')}`} isBold={true} />
                                            <br />
                                        </Grid>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box >
    );
};

export default PeriodicDashboardPage;